import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/apiConfig';
import { APP_NAME } from '../../config/appConstants';
import AuthPageFooter from '../../shared/components/common/AuthPageFooter';
import '../Login/Login.css';
import '../VerifyEmail/VerifyEmail.css';
import appLogo from '../../assets/images/DevicePulse-app-logo2.png';

/**
 * ReactivateAccount Component
 *
 * Phase 2: Account reactivation via email link
 *
 * Flow:
 * 1. User receives reactivation email with link
 * 2. Clicks link → /reactivate?token=abc123
 * 3. This page extracts token and calls /api/email/reactivate-account
 * 4. Backend sets: is_active = 1, password_reset_required = 1
 * 5. Shows success and redirects to /login
 * 6. User logs in with old password
 * 7. App.js detects password_reset_required = true
 * 8. Auto-redirects to /password-reset-required
 * 9. User sets new password (reuses PasswordResetRequired.js component)
 */

function ReactivateAccount() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('reactivating'); // reactivating, success, error
  const [message, setMessage] = useState('Reactivating your account...');

  useEffect(() => {
    const reactivateAccount = async () => {
      const token = searchParams.get('token');
      const email = searchParams.get('email');

      if (!token) {
        setStatus('error');
        setMessage('Reactivation token is missing. Please check your email link.');
        return;
      }

      try {
        // Use GET request like VerifyEmail for consistency
        // The backend now supports both GET and POST
        const response = await axios.get(API_ENDPOINTS.EMAIL.REACTIVATE_ACCOUNT, {
          params: {
            token: token,
            email: email
          }
        });

        if (response.data.account_activated && response.data.password_reset_required) {
          setStatus('success');
          const emailMessage = response.data.email_sent
            ? 'Account reactivated successfully! Check your email for a temporary password. You will use it to log in and set a new password.'
            : 'Account reactivated successfully! A temporary password has been sent to your email. You will use it to log in and set a new password.';
          setMessage(emailMessage);
        } else {
          setStatus('error');
          setMessage('Account reactivation did not complete properly. Please try again.');
          console.error('[ReactivateAccount] Response missing expected fields:', response.data);
        }
      } catch (error) {
        console.error('[ReactivateAccount] Reactivation failed:', error);
        console.error('[ReactivateAccount] Error response:', error.response);
        console.error('[ReactivateAccount] Error message:', error.message);
        if (error.response) {
          console.error('[ReactivateAccount] Status:', error.response.status);
          console.error('[ReactivateAccount] Data:', error.response.data);
        }
        setStatus('error');
        const errorMessage = error.response?.data?.error || error.message || 'Failed to reactivate account. Please try again.';
        setMessage(errorMessage);
      }
    };

    reactivateAccount();
  }, [searchParams]);

  return (
    <div className="login-container">
      {/* Left Side - Animated Gradient Background */}
      <div className="login-left-side">
        {/* Background Layers */}
        <div className="particles"></div>
        <div className="medical-pattern"></div>

        {/* Floating Medical Device Illustrations */}
        <div className="medical-float-1">
          <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="35" y="20" width="50" height="70" rx="5" fill="white" opacity="0.9"/>
            <rect x="30" y="85" width="60" height="25" rx="3" fill="white" opacity="0.9"/>
            <circle cx="60" cy="55" r="15" fill="rgba(102,126,234,0.3)"/>
            <rect x="55" y="30" width="10" height="15" fill="rgba(102,126,234,0.4)"/>
            <line x1="40" y1="95" x2="80" y2="95" stroke="rgba(102,126,234,0.5)" strokeWidth="2"/>
          </svg>
        </div>

        <div className="medical-float-2">
          <svg width="140" height="100" viewBox="0 0 140 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <ellipse cx="70" cy="50" rx="60" ry="35" fill="white" opacity="0.9"/>
            <ellipse cx="70" cy="50" rx="30" ry="20" fill="rgba(102,126,234,0.3)"/>
            <rect x="10" y="40" width="15" height="20" rx="3" fill="white" opacity="0.8"/>
            <rect x="115" y="40" width="15" height="20" rx="3" fill="white" opacity="0.8"/>
            <circle cx="70" cy="30" r="5" fill="rgba(102,126,234,0.5)"/>
          </svg>
        </div>

        <div className="medical-float-3">
          <svg width="100" height="120" viewBox="0 0 100 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="20" y="30" width="60" height="70" rx="8" fill="white" opacity="0.9"/>
            <rect x="30" y="45" width="40" height="25" rx="3" fill="rgba(102,126,234,0.3)"/>
            <circle cx="35" cy="85" r="6" fill="rgba(102,126,234,0.4)"/>
            <circle cx="65" cy="85" r="6" fill="rgba(102,126,234,0.4)"/>
            <rect x="40" y="20" width="20" height="15" rx="2" fill="white" opacity="0.8"/>
            <line x1="30" y1="55" x2="70" y2="55" stroke="white" strokeWidth="2"/>
          </svg>
        </div>

        <div className="medical-float-4">
          <svg width="110" height="110" viewBox="0 0 110 110" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="55" cy="35" r="20" fill="white" opacity="0.9"/>
            <rect x="50" y="50" width="10" height="40" fill="white" opacity="0.9"/>
            <circle cx="55" cy="35" r="12" fill="rgba(102,126,234,0.3)"/>
            <path d="M 45,30 L 55,25 L 65,30" stroke="white" strokeWidth="2" fill="none"/>
            <rect x="35" y="85" width="40" height="15" rx="5" fill="white" opacity="0.8"/>
          </svg>
        </div>

        <div className="medical-float-5">
          <svg width="120" height="100" viewBox="0 0 120 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="15" y="15" width="90" height="60" rx="5" fill="white" opacity="0.9"/>
            <rect x="20" y="20" width="80" height="50" rx="3" fill="rgba(102,126,234,0.3)"/>
            <path d="M 30,45 L 45,45 L 50,35 L 55,55 L 60,40 L 70,45 L 90,45" stroke="rgba(102,126,234,0.6)" strokeWidth="3" fill="none"/>
            <rect x="45" y="75" width="30" height="5" fill="white" opacity="0.8"/>
            <rect x="35" y="80" width="50" height="15" rx="3" fill="white" opacity="0.9"/>
          </svg>
        </div>

        <div className="medical-float-6">
          <svg width="100" height="110" viewBox="0 0 100 110" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="25" y="25" width="50" height="60" rx="6" fill="white" opacity="0.9"/>
            <rect x="35" y="35" width="30" height="20" rx="3" fill="rgba(102,126,234,0.3)"/>
            <circle cx="40" cy="70" r="8" fill="rgba(102,126,234,0.4)"/>
            <circle cx="60" cy="70" r="8" fill="rgba(102,126,234,0.4)"/>
            <rect x="40" y="85" width="20" height="15" rx="2" fill="white" opacity="0.8"/>
          </svg>
        </div>

        {/* Left Side Content */}
        <div className="left-side-content">
          <div className="left-side-text">
            <h2>Welcome back to</h2>
            <h1>
              <img src={appLogo} className="emoji-app-logo" alt="App Logo" />
              {APP_NAME}
            </h1>
            <p>Reactivate your account to continue managing your devices</p>
          </div>
        </div>
        
      </div>

      {/* Right Side - Reactivation Status */}
      <div className="login-right-side">
        <AuthPageFooter />
        
        <div className="login-box">
          {status !== 'success' && (
            <div className="login-header">
              <h3>{status === 'error' ? 'Reactivation Failed' : 'Account Reactivation'}</h3>
              <p>{status === 'error' ? 'Unable to reactivate your account' : 'Processing your reactivation request...'}</p>
            </div>
          )}

          {status === 'reactivating' && (
            <div className="status-container reactivating">
              <div className="spinner"></div>
              <p>{message}</p>
            </div>
          )}

          {status === 'success' && (
            <>
              <div style={{
                textAlign: 'center',
                paddingTop: '20px',
                paddingBottom: '10px',
                borderBottom: '1px solid #f0f0f0',
                marginBottom: '30px'
              }}>
                <h2 style={{ margin: '0 0 8px 0', color: '#2c3e50', fontSize: '24px' }}>
                  Account Reactivated Successfully! ✓
                </h2>
                <p style={{ margin: '0', color: '#7f8c8d', fontSize: '14px' }}>
                  Your account is now active and ready to use
                </p>
              </div>

              <div className="status-container success">
                <div className="success-icon">✓</div>

                <div style={{
                  backgroundColor: '#e8f5e9',
                  border: '1px solid #81c784',
                  borderRadius: '6px',
                  padding: '16px',
                  marginBottom: '24px',
                  textAlign: 'center'
                }}>
                  <p style={{
                    margin: '0',
                    color: '#2e7d32',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    fontWeight: '500'
                  }}>
                    📧 <strong>Check your email for a temporary password</strong><br/>
                    You'll receive an email with a temporary password. Use it to log in, then set your own new password.
                  </p>
                </div>

                <button
                  type="button"
                  className="action-btn action-primary action-btn-block"
                  onClick={() => navigate('/login')}
                >
                  Go to Login
                </button>

                <p style={{
                  textAlign: 'center',
                  color: '#7f8c8d',
                  fontSize: '12px',
                  margin: '16px 0 0 0'
                }}>
                  Welcome back! Your account is ready to use
                </p>
              </div>
            </>
          )}

          {status === 'error' && (
            <div className="status-container error">
              <div className="error-icon">✕</div>
              <h2>Reactivation Failed</h2>
              <p>{message}</p>
              <button
                type="button"
                className="action-btn action-primary action-btn-block"
                onClick={() => navigate('/login')}
              >
                Back to Login
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReactivateAccount;
